/**
 * NotificationBell — bell icon in TopNav with unread badge.
 * Opens NotificationDrawer on click.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../api/agent';

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const drawerRef = useRef(null);
  const navigate = useNavigate();

  // Poll unread count every 60s
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const n = await getUnreadCount();
        if (!cancelled) setCount(n);
      } catch { /* silent */ }
    }
    poll();
    const interval = setInterval(poll, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Load notifications when drawer opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getNotifications({ limit: 30 })
      .then((data) => {
        setNotifications(data.data || []);
        setCount(data.unread_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleMarkRead = useCallback(async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setCount((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setCount(0);
    } catch { /* silent */ }
  }, []);

  const handleNotifClick = useCallback(
    (notif) => {
      if (!notif.is_read) handleMarkRead(notif.id);
      if (notif.related_job_id) {
        setOpen(false);
        navigate(`/jobs/${notif.related_job_id}`);
      } else {
        setOpen(false);
      }
    },
    [handleMarkRead, navigate]
  );

  return (
    <div className="relative" ref={drawerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-1.5 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-50"
        title="Notifications"
      >
        <BellIcon className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 flex flex-col max-h-[520px]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-gray-900">Notifications</span>
              {count > 0 && (
                <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                  {count} unread
                </span>
              )}
            </div>
            {count > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <span className="text-sm text-gray-400">Loading…</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-12 text-center">
                <BellIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No notifications</p>
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onClick={() => handleNotifClick(n)}
                  onMarkRead={() => handleMarkRead(n.id)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single notification row
// ---------------------------------------------------------------------------

const SEVERITY_STYLES = {
  critical: {
    border: 'border-l-red-500',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-700',
  },
  warning: {
    border: 'border-l-yellow-500',
    dot: 'bg-yellow-500',
    badge: 'bg-yellow-100 text-yellow-700',
  },
  info: {
    border: 'border-l-blue-400',
    dot: 'bg-blue-400',
    badge: 'bg-blue-50 text-blue-700',
  },
};

function NotificationRow({ notification: n, onClick, onMarkRead }) {
  const s = SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.info;
  const age = _formatAge(n.created_at);

  return (
    <div
      className={`px-4 py-3 border-b border-gray-50 border-l-4 ${s.border} cursor-pointer hover:bg-gray-50 transition-colors ${
        n.is_read ? 'opacity-60' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            {!n.is_read && (
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
            )}
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide ${s.badge}`}>
              {n.severity}
            </span>
            <span className="text-[10px] text-gray-400">{n.category}</span>
            {n.related_job_number && (
              <span className="text-[10px] font-mono text-gray-500">
                #{n.related_job_number}
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-800 leading-tight">{n.title}</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">{n.body}</p>
          <p className="text-[10px] text-gray-400 mt-1">{age}</p>
        </div>
        {!n.is_read && (
          <button
            onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
            className="text-gray-300 hover:text-gray-500 flex-shrink-0 pt-0.5"
            title="Mark read"
          >
            <CheckIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function _formatAge(isoStr) {
  if (!isoStr) return '';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function BellIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}
